/**
 * WelcomeActivationModal.tsx
 * One-time welcome/activation modal shown to a brand-new wallet right after it logs in
 * (driven by the `is_new` signal from /verify) and only while it owns zero Suikamons.
 *
 * Identity/UX only — no custody, minting, or economy code. Web2: the game is free, so the
 * last step leads to the free starter pick; the old "75% off" discount framing is gone.
 *
 * Mirrors the SeasonPopup pattern (state-gated mount in AppContent + localStorage guard).
 */
import { useEffect, useState } from "react";
import { EmojiActionButton } from "./shared/EmojiActionButton";
import {
  trackNewWalletActivationShown,
  trackNewWalletActivationPlayLabClick,
  trackNewWalletActivationAdoptClick,
} from "../lib/analytics";
import { sfxWin } from "../lib/soundEffects";
import "./WelcomeActivationModal.css";
import { useT } from "../i18n/i18nContext";
import type { MessageKey } from "../i18n/englishCatalog";
import { EGG_IMAGE_SRC } from "../utils/petHelpers";

/** localStorage key prefix → one guard entry per account (`<prefix><accountId>`). */
const ACTIVATION_STORAGE_PREFIX = "suikamon.activation.v1.";

/** Reused public/ art (no new assets). Backdrop image lives in the CSS class. */
const WELCOME_MARK_SRC = "/logo-simple.png";
const LAB_CTA_ART_SRC = "/shika_fight.png";
const ADOPT_CTA_ART_SRC = EGG_IMAGE_SRC;

/** Core game features surfaced on the "how it works" step (display only). */
const GAME_FEATURES = [
  { emoji: "⚔️", labelKey: "account.welcome.featureBattle" },
  { emoji: "✨", labelKey: "account.welcome.featureEvolve" },
  { emoji: "🗺️", labelKey: "account.welcome.featureCampaign" },
] as const satisfies readonly { emoji: string; labelKey: MessageKey }[];

const TOTAL_STEPS = 3;

function activationKey(accountId: string): string {
  return `${ACTIVATION_STORAGE_PREFIX}${accountId}`;
}

/** True if the one-time activation modal hasn't been shown for this account yet. */
// eslint-disable-next-line react-refresh/only-export-components -- intentional co-located export
export function shouldShowActivation(accountId: string): boolean {
  try {
    return !localStorage.getItem(activationKey(accountId));
  } catch {
    // localStorage unavailable → don't risk showing the modal repeatedly.
    return false;
  }
}

/** Mark the activation modal as shown for this account so it never re-appears. */
// eslint-disable-next-line react-refresh/only-export-components -- intentional co-located export
export function markActivationShown(accountId: string): void {
  try {
    localStorage.setItem(activationKey(accountId), String(Date.now()));
  } catch {
    // Best-effort — if persistence fails the dismiss still closes the modal this session.
  }
}

interface WelcomeActivationModalProps {
  /** Trainer display name for the greeting; falls back to a generic greeting when absent. */
  displayName?: string | null;
  /** Final-step secondary CTA → a practice battle in the PvP Lab. */
  onPlayLab: () => void;
  /** Final-step primary CTA → the free starter pick (/play-free). */
  onAdopt: () => void;
  /** Dismiss without choosing a CTA ("Maybe later" / backdrop / close). */
  onClose: () => void;
}

export function WelcomeActivationModal({
  displayName,
  onPlayLab,
  onAdopt,
  onClose,
}: WelcomeActivationModalProps) {
  const t = useT();
  const [stepIndex, setStepIndex] = useState(0);
  const trainerName = displayName?.trim() || t("account.welcome.defaultName");

  useEffect(() => {
    trackNewWalletActivationShown();
    // Celebratory welcome chime — login was a user gesture, so audio is unlocked.
    sfxWin();
  }, []);

  const goNext = () => setStepIndex((i) => Math.min(i + 1, TOTAL_STEPS - 1));

  const handlePlayLab = () => {
    trackNewWalletActivationPlayLabClick();
    onPlayLab();
  };

  const handleAdopt = () => {
    trackNewWalletActivationAdoptClick();
    onAdopt();
  };

  return (
    <div className="activation-overlay" onClick={onClose} role="presentation">
      <div
        className="activation-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="activation-title"
      >
        <button className="activation-close" onClick={onClose} aria-label={t("common.action.close")}>
          ✕
        </button>

        {/* Progress dots */}
        <div className="activation-progress" aria-hidden="true">
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <span
              key={i}
              className={`activation-progress-dot${
                i === stepIndex ? " activation-progress-dot--active" : ""
              }${i < stepIndex ? " activation-progress-dot--done" : ""}`}
            />
          ))}
        </div>

        {/* Step 1 — Welcome */}
        {stepIndex === 0 && (
          <div className="activation-body">
            <img src={WELCOME_MARK_SRC} alt="" className="activation-mark" />
            <p className="activation-eyebrow">{t("account.welcome.eyebrow")}</p>
            <h2 id="activation-title" className="activation-title">
              {t("account.welcome.title", { trainerName })}
            </h2>
            <p className="activation-subtitle">
              {t("account.welcome.subtitle")}
            </p>
            <div className="activation-actions">
              <EmojiActionButton emoji="👉" onClick={goNext} variant="primary" size="large">
                {t("account.welcome.start")}
              </EmojiActionButton>
            </div>
          </div>
        )}

        {/* Step 2 — How it works */}
        {stepIndex === 1 && (
          <div className="activation-body">
            <h2 id="activation-title" className="activation-title activation-title--sm">
              {t("account.welcome.howTitle")}
            </h2>
            <p className="activation-subtitle">
              {t("account.welcome.howText")}
            </p>
            <ul className="activation-careloop">
              {GAME_FEATURES.map(({ emoji, labelKey }) => (
                <li key={labelKey} className="activation-careloop-item">
                  <span className="activation-careloop-emoji" aria-hidden="true">
                    {emoji}
                  </span>
                  <span className="activation-careloop-label">{t(labelKey)}</span>
                </li>
              ))}
            </ul>
            <div className="activation-actions">
              <EmojiActionButton emoji="👉" onClick={goNext} variant="primary" size="large">
                {t("common.action.next")}
              </EmojiActionButton>
            </div>
          </div>
        )}

        {/* Step 3 — Get started. Web2: the game is free — a free Common starter is the
            whole offer; every other Suikamon is discovered in eggs. No prices, no discount. */}
        {stepIndex === 2 && (
          <div className="activation-body">
            <h2 id="activation-title" className="activation-title activation-title--sm">
              {t("account.welcome.getTitle")}
            </h2>
            <p className="activation-subtitle">
              {t("account.welcome.getText")}
            </p>

            <div className="activation-cta-grid">
              <button className="activation-cta activation-cta--primary" onClick={handleAdopt}>
                <img src={ADOPT_CTA_ART_SRC} alt="" className="activation-cta-art" />
                <span className="activation-cta-title">{t("account.welcome.starterTitle")}</span>
                <span className="activation-cta-sub">{t("account.welcome.starterSub")}</span>
              </button>

              <button className="activation-cta activation-cta--secondary" onClick={handlePlayLab}>
                <img src={LAB_CTA_ART_SRC} alt="" className="activation-cta-art" />
                <span className="activation-cta-title">{t("account.welcome.labTitle")}</span>
                <span className="activation-cta-sub">{t("account.welcome.labSub")}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
